import type {MFCleanup, MFContext} from '@/types/microfrontend'

type ViteFederationModule = {
  mount?: (el: HTMLElement, config: any) => MFCleanup
  default?: (() => ViteFederationModule) | ViteFederationModule
}

export function unwrapViteFederationModule(importedModule: unknown): ViteFederationModule {
  let moduleExports = importedModule as ViteFederationModule

  // Check if it's a factory function
  if (typeof importedModule === 'function') {
    moduleExports = (importedModule as () => ViteFederationModule)()
  }
  // Check if default is a factory function
  else if (moduleExports.default && typeof moduleExports.default === 'function') {
    moduleExports = (moduleExports.default as () => ViteFederationModule)()
  }

  return moduleExports
}

export function mountMicrofrontend<TConfig>(
  element: HTMLElement,
  moduleExports: ViteFederationModule,
  config: TConfig,
  moduleName: string,
): MFCleanup | null {
  const unwrapped = unwrapViteFederationModule(moduleExports)

  const mount = unwrapped.mount

  if (typeof mount !== 'function') {
    throw new Error(`${moduleName} does not export a mount function, got: ${typeof mount}`)
  }

  return mount(element, config)
}

export async function loadSalesAppointmentsMF(
  element: HTMLElement,
  context: MFContext & {props: Record<string, never>},
): Promise<MFCleanup | null> {
  try {
    const importedModule = await import('sales-appointments-mf/App')

    return mountMicrofrontend(element, importedModule, context, 'sales-appointments-mf/App')
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err))
    console.error('Error loading Sales Appointments MF:', error)
    return null
  }
}

export type LeadSelection = {type: 'new'} | {type: 'existing'; leadId: string}

export interface CreateAppointmentFormData {
  appointmentType: string
  contact: {
    name: string
    email?: string
    phone: string
  }
  model?: string
  date: string
  startTime: string
  endTime: string
  comment?: string
  leadSelection: LeadSelection
}

export async function loadCreateAppointmentModal(
  element: HTMLElement,
  context: MFContext & {
    props: {
      isOpen: boolean
      onClose: () => void
      onSubmit: (data: CreateAppointmentFormData) => Promise<void>
      initialDate?: Date
      initialStartTime?: string
      initialEndTime?: string
      prefilledContact?: {
        name: string
        lastName: string
        email: string
        phone: string
        countryCode: string
        countryISO2: string
        isPhoneValid?: boolean
      }
      disableContactFields?: boolean
      dealerContactId?: string
      preselectedLeadId?: string
    }
  },
): Promise<MFCleanup | null> {
  try {
    const importedModule = await import('sales-appointments-mf/CreateAppointmentModal')

    return mountMicrofrontend(element, importedModule, context, 'sales-appointments-mf/CreateAppointmentModal')
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err))
    console.error('Error loading CreateAppointmentModal:', error)
    return null
  }
}
